import { useEffect, useState } from 'react';
import { Box, Typography, Button, Paper, IconButton, Dialog, DialogContent, TextField, FormControlLabel, Switch, Checkbox, Chip, List, ListItem, ListItemText, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import supabase from '../../../supabaseClient';
import { PRODUCTION_SOURCES } from '../../../Utils/machineProductionStats';

const emptyForm = { name: '', ip: '', port: '' };

function ManagerDevices() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const [idConfiguration, setIdConfiguration] = useState(null);
    const [showArchived, setShowArchived] = useState(false);

    const [filterOpen, setFilterOpen] = useState(false);
    const [searchName, setSearchName] = useState('');
    const [searchNumber, setSearchNumber] = useState('');

    // Stany dla okna dialogowego dodawania / edycji maszyny
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [archived, setArchived] = useState(null);
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    // Stanowiska pracy maszyny
    const [stands, setStands] = useState([]);
    const [newStandName, setNewStandName] = useState('');
    const [newStandIsOperator, setNewStandIsOperator] = useState(false);
    const [loadingStands, setLoadingStands] = useState(false);

    // Stany dla edycji konkretnego stanowiska na liście
    const [editingStandId, setEditingStandId] = useState(null);
    const [editingStandName, setEditingStandName] = useState('');
    const [editingStandIsOperator, setEditingStandIsOperator] = useState(false);

    // Stany dla ikony / zdjęcia maszyny w edycji
    const [hasIcon, setHasIcon] = useState(false);
    const [iconErrorMessage, setIconErrorMessage] = useState('');
    const [openIconError, setOpenIconError] = useState(false);

    // Stan dla normy
    const [target, setTarget] = useState('')
    const [unit, setUnit] = useState('');
    const [productionSource, setProductionSource] = useState('');

    // Stan dla pozycji premiowych (dodawanie)
    const [nowyProg, setNowyProg] = useState({ nazwa: '', od: '', do: '', wspolczynnik: '' });
    const [progi, setProgi] = useState([]);
    const [loadingProgi, setLoadingProgi] = useState(false);

    // Stan dla edycji istniejącego progu (ta sama zasada co edycja stanowisk)
    const [editingProgId, setEditingProgId] = useState(null);
    const [editingProg, setEditingProg] = useState({ nazwa: '', od: '', do: '', wspolczynnik: '' });

    const mapProgFromDb = (d) => ({
        id: d.id,
        nazwa: d.name,
        od: d.value_from,
        do: d.value_to,
        wspolczynnik: d.coefficient,
    });

    // Postgres (numeric) akceptuje tylko kropkę jako separator dziesiętny
    const toDecimal = (value) => String(value).replace(',', '.');

    const handleChange = (pole) => (e) => {
        setNowyProg(prev => ({ ...prev, [pole]: e.target.value }));
    };

    const dodajProg = async () => {
        if (!nowyProg.nazwa || nowyProg.od === '' || nowyProg.do === '' || nowyProg.wspolczynnik === '') return;

        const { data, error } = await supabase
            .from('device_bonus_thresholds')
            .insert([{
                device_id: editingId,
                id_configuration: idConfiguration,
                name: nowyProg.nazwa,
                value_from: toDecimal(nowyProg.od),
                value_to: toDecimal(nowyProg.do),
                coefficient: toDecimal(nowyProg.wspolczynnik),
            }])
            .select();

        if (error) {
            console.error('Błąd dodawania progu:', error);
        } else if (data) {
            setProgi(prev => [...prev, mapProgFromDb(data[0])]);
            setNowyProg({ nazwa: '', od: '', do: '', wspolczynnik: '' });
        }
    };

    const usunProg = async (progId) => {
        const { error } = await supabase
            .from('device_bonus_thresholds')
            .delete()
            .eq('id', progId);

        if (error) {
            console.error('Błąd usuwania progu:', error);
        } else {
            setProgi(prev => prev.filter((p) => p.id !== progId));
            if (editingProgId === progId) {
                handleCancelEditProg();
            }
        }
    };

    const handleStartEditProg = (prog) => {
        setEditingProgId(prog.id);
        setEditingProg(prog);
    };

    const handleCancelEditProg = () => {
        setEditingProgId(null);
        setEditingProg({ nazwa: '', od: '', do: '', wspolczynnik: '' });
    };

    const handleEditProgChange = (pole) => (e) => {
        setEditingProg(prev => ({ ...prev, [pole]: e.target.value }));
    };

    const handleSaveProgEdit = async (progId) => {
        if (!editingProg.nazwa || editingProg.od === '' || editingProg.do === '' || editingProg.wspolczynnik === '') return;

        const { error } = await supabase
            .from('device_bonus_thresholds')
            .update({
                name: editingProg.nazwa,
                value_from: toDecimal(editingProg.od),
                value_to: toDecimal(editingProg.do),
                coefficient: toDecimal(editingProg.wspolczynnik),
            })
            .eq('id', progId);

        if (error) {
            console.error('Błąd edycji progu:', error);
        } else {
            setProgi(prev => prev.map((p) => (p.id === progId ? { ...p, ...editingProg } : p)));
            handleCancelEditProg();
        }
    };

    useEffect(() => {
        const getInitialData = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                const { data: profileData } = await supabase
                    .from('profiles')
                    .select('id_configuration')
                    .eq('id', session.user.id)
                    .single();

                if (profileData) {
                    setIdConfiguration(profileData.id_configuration);
                }
            }
        };
        getInitialData();
    }, []);

    async function fetchDevices() {
        if (!idConfiguration) return;
        setLoading(true);

        let query = supabase
            .from('devices')
            .select('*')
            .eq('id_configuration', idConfiguration);

        if (showArchived) {
            query = query.eq('archived', 1);
        } else {
            query = query.is('archived', null);
        }

        const { data, error } = await query.order('id', { ascending: false });

        if (error) {
            console.error('Błąd pobierania urządzeń:', error);
            setLoading(false);
            return;
        }

        setRows(data || []);
        setLoading(false);
    }

    useEffect(() => {
        if (idConfiguration) {
            fetchDevices();
        }
    }, [idConfiguration, showArchived]);

    const fetchStands = async (deviceId) => {
        if (!deviceId || !idConfiguration) return;
        setLoadingStands(true);
        const { data, error } = await supabase
            .from('device_stands')
            .select('*')
            .eq('device_id', deviceId)
            .eq('id_configuration', idConfiguration)
            .order('id', { ascending: true });

        if (!error) {
            setStands(data || []);
        }
        setLoadingStands(false);
    };

    const handleAddStand = async () => {
        if (!newStandName.trim() || !editingId || !idConfiguration) return;

        const { data, error } = await supabase
            .from('device_stands')
            .insert([{
                device_id: editingId,
                name: newStandName.trim(),
                id_configuration: idConfiguration,
                is_operator_role: newStandIsOperator,
            }])
            .select();

        if (error) {
            console.error('Błąd dodawania stanowiska:', error);
        } else if (data) {
            setStands((prev) => [...prev, ...data]);
            setNewStandName('');
            setNewStandIsOperator(false);
        }
    };

    const handleStartEditStand = (stand) => {
        setEditingStandId(stand.id);
        setEditingStandName(stand.name);
        setEditingStandIsOperator(stand.is_operator_role || false);
    };

    const handleCancelEditStand = () => {
        setEditingStandId(null);
        setEditingStandName('');
        setEditingStandIsOperator(false);
    };

    const handleSaveStandEdit = async (standId) => {
        if (!editingStandName.trim()) return;

        const { error } = await supabase
            .from('device_stands')
            .update({ name: editingStandName.trim(), is_operator_role: editingStandIsOperator })
            .eq('id', standId);

        if (error) {
            console.error('Błąd edycji stanowiska:', error);
        } else {
            setStands((prev) =>
                prev.map((s) => (s.id === standId ? { ...s, name: editingStandName.trim(), is_operator_role: editingStandIsOperator } : s))
            );
            setEditingStandId(null);
            setEditingStandName('');
            setEditingStandIsOperator(false);
        }
    };

    const handleDeleteStand = async (standId) => {
        const { error } = await supabase
            .from('device_stands')
            .delete()
            .eq('id', standId);

        if (error) {
            console.error('Błąd usuwania stanowiska:', error);
        } else {
            setStands((prev) => prev.filter((s) => s.id !== standId));
        }
    };


    // zapisywanie progów premiowych
    const fetchProgi = async (deviceId) => {
        if (!deviceId || !idConfiguration) return;
        setLoadingProgi(true);
        const { data, error } = await supabase
            .from('device_bonus_thresholds')
            .select('*')
            .eq('device_id', deviceId)
            .eq('id_configuration', idConfiguration)
            .order('id', { ascending: true });

        if (!error) {
            setProgi((data || []).map(mapProgFromDb));
        }
        setLoadingProgi(false);
    };


    const filteredRows = rows.filter((row) => {
        const matchesName = searchName === '' || row.name?.toLowerCase().includes(searchName.toLowerCase());
        const matchesNumber = searchNumber === '' || row.id?.toString().includes(searchNumber);
        return matchesName && matchesNumber;
    });

    const checkIfIconExists = async (deviceId) => {
        const { data } = await supabase.storage
            .from('devices')
            .list('', { search: `${deviceId}.png` });

        setHasIcon(data && data.length > 0);
    };

    const validateIcon = (file) => {
        const MAX_SIZE_BYTES = 80 * 1024;
        if (file.type !== 'image/png') {
            setIconErrorMessage('Błąd: Akceptowane są wyłącznie pliki w formacie PNG!');
            setOpenIconError(true);
            return false;
        }
        if (file.size > MAX_SIZE_BYTES) {
            setIconErrorMessage('Błąd: Plik jest za duży! Maksymalny rozmiar to 80 KB.');
            setOpenIconError(true);
            return false;
        }
        return true;
    };

    const handleUploadIcon = async (event) => {
        const file = event.target.files[0];
        if (!file || !editingId) return;
        if (!validateIcon(file)) return;

        const filePath = `${editingId}.png`;
        const { error } = await supabase.storage
            .from('devices')
            .upload(filePath, file, { cacheControl: '3600', upsert: true });

        if (error) {
            console.error(error);
            setIconErrorMessage('Nie udało się zapisać pliku w bazie.');
            setOpenIconError(true);
        } else {
            setHasIcon(true);
        }
    };

    const handleDeleteIcon = async () => {
        if (!editingId) return;
        const filePath = `${editingId}.png`;
        const { error } = await supabase.storage.from('devices').remove([filePath]);

        if (error) {
            console.error(error);
            setIconErrorMessage('Nie udało się usunąć ikony.');
            setOpenIconError(true);
        } else {
            setHasIcon(false);
        }
    };

    const openAddDialog = () => {
        setEditingId(null);
        setForm(emptyForm);
        setArchived(null);
        setHasIcon(false);
        setStands([]);
        setProgi([]);
        setTarget('');
        setUnit('');
        setProductionSource('');
        setEditingStandId(null);
        setNewStandName('');
        setNewStandIsOperator(false);
        setEditingStandName('');
        setEditingStandIsOperator(false);
        setNowyProg({ nazwa: '', od: '', do: '', wspolczynnik: '' });
        setEditingProgId(null);
        setEditingProg({ nazwa: '', od: '', do: '', wspolczynnik: '' });
        setErrorMsg('');
        setDialogOpen(true);
    };

    const openEditDialog = (row) => {
        setEditingId(row.id);
        setForm({
            name: row.name ?? '',
            ip: row.ip ?? '',
            port: row.port ? String(row.port) : '',
        });
        setArchived(row.archived || null);
        setTarget(row.daily_target ?? '');
        setUnit(row.unit ?? '');
        setProductionSource(row.production_source ?? '');
        setEditingStandId(null);
        setNewStandName('');
        setNewStandIsOperator(false);
        setEditingStandName('');
        setEditingStandIsOperator(false);
        setNowyProg({ nazwa: '', od: '', do: '', wspolczynnik: '' });
        setEditingProgId(null);
        setEditingProg({ nazwa: '', od: '', do: '', wspolczynnik: '' });
        setErrorMsg('');
        setDialogOpen(true);
        checkIfIconExists(row.id);
        fetchStands(row.id);
        fetchProgi(row.id);
    };

    const handleFieldChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            setErrorMsg('Nazwa maszyny jest wymagana.');
            return;
        }

        if (!idConfiguration) {
            setErrorMsg('Nie udało się ustalić konfiguracji użytkownika.');
            return;
        }

        setSaving(true);
        setErrorMsg('');

        const payload = {
            name: form.name.trim(),
            ip: form.ip.trim(),
            port: form.port ? Number(form.port) : null,
            archived: archived,
            daily_target: target !== '' ? toDecimal(target) : null,
            unit: unit.trim() || null,
            production_source: productionSource || null,
        };

        if (editingId) {
            const { error } = await supabase.from('devices').update(payload).eq('id', editingId);
            if (error) {
                console.error('Błąd aktualizacji maszyny:', error);
                setErrorMsg('Nie udało się zapisać zmian.');
                setSaving(false);
                return;
            }
        } else {
            const { error } = await supabase.from('devices').insert([{ ...payload, id_configuration: idConfiguration }]);
            if (error) {
                console.error('Błąd dodawania maszyny:', error);
                setErrorMsg('Nie udało się dodać maszyny.');
                setSaving(false);
                return;
            }
        }

        setSaving(false);
        setDialogOpen(false);
        fetchDevices();
    };

    const columns = [
        { field: 'id', headerName: 'ID', flex: 0.6, minWidth: 80, renderCell: (params) => <strong>{params.value}</strong> },
        { field: 'name', headerName: 'NAZWA', flex: 1.5, minWidth: 180, renderCell: (params) => <strong>{params.value}</strong> },
        { field: 'ip', headerName: 'IP', flex: 1, minWidth: 130 },
        { field: 'port', headerName: 'PORT', flex: 0.8, minWidth: 100 },
        {
            field: 'archived',
            headerName: 'STATUS',
            flex: 0.8,
            minWidth: 110,
            renderCell: (params) => (
                <Chip
                    label={params.value === 1 ? 'Nieaktywne' : 'Aktywne'}
                    size="small"
                    sx={{
                        bgcolor: params.value === 1 ? '#F1EDE6' : '#E4EDFB',
                        color: params.value === 1 ? '#6B6B6B' : '#2E6DC4',
                        fontWeight: 600,
                        borderRadius: '8px',
                        height: 26
                    }}
                />
            ),
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.6,
            minWidth: 90,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog(params.row); }}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: 'primary.main' }}>Urządzenia</Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setFilterOpen(true)}
                        startIcon={<FilterListIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2, py: 1 }}
                    >
                        Filtry
                    </Button>
                    <Button
                        variant="contained"
                        onClick={openAddDialog}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5, py: 1 }}
                    >
                        + Nowa maszyna
                    </Button>
                </Box>
            </Box>

            <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #ECE7DE' }}>
                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    loading={loading}
                    onRowClick={(params) => openEditDialog(params.row)}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    hideFooter={filteredRows.length <= 100}
                    columnHeaderHeight={44}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#FAF7F2' },
                        '& .MuiDataGrid-columnHeaderTitle': { color: 'text.secondary', fontWeight: 700, fontSize: '12px', letterSpacing: '0.03em' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-row:hover': { backgroundColor: '#FEFCF9' },
                    }}
                />
            </Paper>

            {/* Okno dialogowe dodawania / edycji maszyny */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'primary.main', mb: 3 }}>
                        {editingId ? 'Edycja maszyny' : 'Dodaj maszynę'}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                        <TextField
                            label="Nazwa"
                            size="small"
                            value={form.name}
                            onChange={handleFieldChange('name')}
                            fullWidth
                            required
                        />
                        <TextField
                            label="IP"
                            size="small"
                            value={form.ip}
                            onChange={handleFieldChange('ip')}
                            fullWidth
                        />
                        <TextField
                            label="Port"
                            size="small"
                            type="number"
                            value={form.port}
                            onChange={handleFieldChange('port')}
                            fullWidth
                        />

                        {/* sekcja norm pracy na maszynie */}
                         {editingId ? (
                            <Box sx={{ p: 2, border: '1px solid #ECE7DE', borderRadius: 2, bgcolor: '#FAF7F2' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                                    Normy i progi bonusów
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <TextField
                                        size="small"
                                        label="Tygodniowa norma"
                                        helperText="suma pon.–niedz., np. 250"
                                        sx={{ bgcolor: '#fff' }}
                                        type="number"
                                        value={target}
                                        onChange={(e)=> setTarget(e.target.value)}>
                                    </TextField>
                                    <TextField
                                        size="small"
                                        label="Jednostka"
                                        placeholder="np. m³"
                                        sx={{ bgcolor: '#fff', width: 140 }}
                                        value={unit}
                                        onChange={(e) => setUnit(e.target.value)}
                                    />
                                    <FormControl size="small" sx={{ bgcolor: '#fff', minWidth: 220 }}>
                                        <InputLabel id="production-source-label">Źródło danych produkcji</InputLabel>
                                        <Select
                                            labelId="production-source-label"
                                            label="Źródło danych produkcji"
                                            value={productionSource}
                                            onChange={(e) => setProductionSource(e.target.value)}
                                        >
                                            <MenuItem value=""><em>Brak</em></MenuItem>
                                            {Object.entries(PRODUCTION_SOURCES).map(([key, source]) => (
                                                <MenuItem key={key} value={key}>{source.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.5 }}>
                                        Progi "Od"/"Do" odnoszą się do sumy produkcji w całym tygodniu (np. 250, 300)
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap:1, mb: 1}}>
                                            <TextField sx={{ bgcolor: '#fff' }} size="small" label="Nazwa" value={nowyProg.nazwa} onChange={handleChange('nazwa')}></TextField>
                                            <TextField sx={{ bgcolor: '#fff' }} size="small" label="Od" placeholder="np. 250" value={nowyProg.od} onChange={handleChange('od')}></TextField>
                                            <TextField sx={{ bgcolor: '#fff' }} size="small" label="Do" placeholder="np. 300" value={nowyProg.do} onChange={handleChange('do')}></TextField>
                                            <TextField sx={{ bgcolor: '#fff' }} size="small" label="Współczynnik premii" value={nowyProg.wspolczynnik} onChange={handleChange('wspolczynnik')}></TextField>
                                            <Button variant="contained" onClick={dodajProg} startIcon={<AddIcon />} sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderRadius: '8px' }}>
                                                Dodaj
                                            </Button>
                                    </Box>
                                </Box>

                                <TableContainer sx={{ bgcolor: '#fff', borderRadius: 1.5, border: '1px solid #ECE7DE', maxHeight: 220 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700 }}>Nazwa</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Od</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Do</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Współczynnik</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>Akcje</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {progi.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={5} sx={{ color: 'text.secondary', fontSize: '13px' }}>
                                                        Brak zdefiniowanych progów
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                progi.map((p) => (
                                                    <TableRow key={p.id}>
                                                        {editingProgId === p.id ? (
                                                            <>
                                                                <TableCell>
                                                                    <TextField size="small" value={editingProg.nazwa} onChange={handleEditProgChange('nazwa')} autoFocus sx={{ width: 110 }} />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <TextField size="small" value={editingProg.od} onChange={handleEditProgChange('od')} sx={{ width: 70 }} />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <TextField size="small" value={editingProg.do} onChange={handleEditProgChange('do')} sx={{ width: 70 }} />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <TextField size="small" value={editingProg.wspolczynnik} onChange={handleEditProgChange('wspolczynnik')} sx={{ width: 90 }} />
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <IconButton size="small" color="primary" onClick={() => handleSaveProgEdit(p.id)}>
                                                                        <CheckIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton size="small" onClick={handleCancelEditProg}>
                                                                        <CloseIcon fontSize="small" />
                                                                    </IconButton>
                                                                </TableCell>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <TableCell>{p.nazwa}</TableCell>
                                                                <TableCell>{p.od}</TableCell>
                                                                <TableCell>{p.do}</TableCell>
                                                                <TableCell>x{p.wspolczynnik}</TableCell>
                                                                <TableCell align="right">
                                                                    <IconButton size="small" onClick={() => handleStartEditProg(p)}>
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton size="small" color="error" onClick={() => usunProg(p.id)}>
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </TableCell>
                                                            </>
                                                        )}
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                            ): (
                            <Typography variant="caption" color="text.secondary">
                                * Normy pracy będzie można zdefiniować po utworzeniu maszyny.
                            </Typography>
                        )}


                        {/* SEKCJA: Stanowiska pracy na maszyny */}
                        {editingId ? (
                            <Box sx={{ p: 2, border: '1px solid #ECE7DE', borderRadius: 2, bgcolor: '#FAF7F2' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                                    Stanowiska pracy na tej maszynie
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                                    <TextField
                                        size="small"
                                        placeholder="Nazwa stanowiska (np. Operator, Pomocnik)"
                                        value={newStandName}
                                        onChange={(e) => setNewStandName(e.target.value)}
                                        fullWidth
                                        sx={{ bgcolor: '#fff' }}
                                    />
                                    <FormControlLabel
                                        sx={{ whiteSpace: 'nowrap', mr: 0 }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={newStandIsOperator}
                                                onChange={(e) => setNewStandIsOperator(e.target.checked)}
                                            />
                                        }
                                        label="Operator"
                                    />
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleAddStand}
                                        startIcon={<AddIcon />}
                                        sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderRadius: '8px' }}
                                    >
                                        Dodaj
                                    </Button>
                                </Box>

                                <List dense sx={{ bgcolor: '#fff', borderRadius: 1.5, border: '1px solid #ECE7DE', maxHeight: 180, overflow: 'auto' }}>
                                    {stands.length === 0 ? (
                                        <ListItem>
                                            <ListItemText primary="Brak zdefiniowanych stanowisk" primaryTypographyProps={{ fontSize: '13px', color: 'text.secondary' }} />
                                        </ListItem>
                                    ) : (
                                        stands.map((stand) => (
                                            <ListItem
                                                key={stand.id}
                                                secondaryAction={
                                                    editingStandId === stand.id ? (
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <IconButton edge="end" size="small" color="primary" onClick={() => handleSaveStandEdit(stand.id)}>
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton edge="end" size="small" onClick={handleCancelEditStand}>
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <IconButton edge="end" size="small" onClick={() => handleStartEditStand(stand)}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton edge="end" size="small" color="error" onClick={() => handleDeleteStand(stand.id)}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    )
                                                }
                                            >
                                                {editingStandId === stand.id ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 7 }}>
                                                        <TextField
                                                            size="small"
                                                            value={editingStandName}
                                                            onChange={(e) => setEditingStandName(e.target.value)}
                                                            autoFocus
                                                            fullWidth
                                                        />
                                                        <FormControlLabel
                                                            sx={{ whiteSpace: 'nowrap', mr: 0 }}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={editingStandIsOperator}
                                                                    onChange={(e) => setEditingStandIsOperator(e.target.checked)}
                                                                />
                                                            }
                                                            label="Operator"
                                                        />
                                                    </Box>
                                                ) : (
                                                    <ListItemText
                                                        primary={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                {stand.name}
                                                                {stand.is_operator_role && (
                                                                    <Chip label="Operator" size="small" sx={{ borderRadius: '6px', fontSize: '11px', height: 20 }} />
                                                                )}
                                                            </Box>
                                                        }
                                                        primaryTypographyProps={{ fontSize: '14px', fontWeight: 500, component: 'div' }}
                                                    />
                                                )}
                                            </ListItem>
                                        ))
                                    )}
                                </List>
                            </Box>
                        ) : (
                            <Typography variant="caption" color="text.secondary">
                                * Stanowiska pracy będzie można zdefiniować po utworzeniu maszyny.
                            </Typography>
                        )}
    

                        {/* Sekcja ikony widoczna tylko podczas edycji istniejącej maszyny */}
                        {editingId && (
                            <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 2, bgcolor: '#fafafa', mt: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                    Ikona urządzenia
                                </Typography>
                                <Typography variant="caption" display="block" color="textSecondary" sx={{ mb: 2 }}>
                                    Wymagany format: <strong>PNG</strong>. Maksymalny rozmiar: <strong>80 KB</strong>.
                                </Typography>

                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <Button
                                        variant="contained"
                                        component="label"
                                        size="small"
                                        color={hasIcon ? "warning" : "primary"}
                                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                                    >
                                        {hasIcon ? "Zmień ikonę (Edytuj)" : "Dodaj ikonę"}
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/png"
                                            onChange={handleUploadIcon}
                                        />
                                    </Button>

                                    {hasIcon && (
                                        <Button 
                                            variant="outlined" 
                                            color="error" 
                                            size="small"
                                            onClick={handleDeleteIcon}
                                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                                        >
                                            Usuń ikonę
                                        </Button>
                                    )}
                                </Box>
                                {openIconError && (
                                    <Typography sx={{ color: '#C7433F', fontSize: '12px', mt: 1 }}>
                                        {iconErrorMessage}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {editingId && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={archived === 1}
                                        onChange={(e) => setArchived(e.target.checked ? 1 : null)}
                                        color="error"
                                    />
                                }
                                label="Maszyna archiwalna"
                            />
                        )}

                        {errorMsg && <Typography sx={{ color: '#C7433F', fontSize: '13px' }}>{errorMsg}</Typography>}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Anuluj
                        </Button>
                        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            {saving ? 'Zapisywanie...' : 'Zapisz'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Okno dialogowe filtrów */}
            <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'primary.main', mb: 3 }}>
                        Filtry
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                        <TextField
                            label="ID"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchNumber}
                            onChange={(e) => setSearchNumber(e.target.value)}
                        />
                        <TextField
                            label="Nazwa"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showArchived}
                                    onChange={(e) => setShowArchived(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={showArchived ? 'Pokaż nieaktywne' : 'Pokaż aktywne'}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button onClick={() => setFilterOpen(false)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Zamknij
                        </Button>
                        <Button variant="contained" onClick={() => setFilterOpen(false)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Zastosuj
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default ManagerDevices;